/**
 * Solicitudes de seguros de /seguros-pesca (RC, viaje, embarcación) vía GESA.
 * Listado por RPC; detalle, justificante y acciones por /ops/insurance-requests.
 */

import { useState } from 'react';
import { Loader2, RefreshCw, FileText, X } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { API_BASE_URL, apiClient } from '@/services/api-client';

type Product = 'rc' | 'viaje' | 'embarcacion';
type Status = 'awaiting_payment' | 'quote_requested' | 'quoted' | 'payment_reported' | 'delivered' | 'cancelled';

interface Row {
  id: string;
  public_id: string;
  product: Product;
  status: Status;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  insured_count: number;
  amount_cents: number | null;
  payment_channel: 'upload' | 'whatsapp' | null;
  has_receipt: boolean;
  details: Record<string, unknown>;
  is_test: boolean;
  payment_reported_at: string | null;
  broker_emailed_at: string | null;
  delivered_at: string | null;
  admin_note: string | null;
  created_at: string;
}

interface Person {
  firstName: string;
  lastName: string;
  documentType: string;
  documentNumber: string;
  birthDate: string;
  nationality: string;
}

interface Detail {
  id: string;
  publicId: string;
  product: Product;
  status: Status;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  insuredCount: number;
  amountCents: number | null;
  paymentConcept: string | null;
  paymentChannel: 'upload' | 'whatsapp' | null;
  hasReceipt: boolean;
  hasIdentity?: boolean;
  idChannel?: 'upload' | 'whatsapp' | null;
  details: Record<string, unknown>;
  persons: Person[];
  address: { street: string; postalCode: string; city: string; province: string } | null;
  adminNote: string | null;
  brokerEmailedAt: string | null;
  events: { from_status: string | null; to_status: string; actor: string; note: string | null; created_at: string }[];
}

const PRODUCT_LABEL: Record<Product, string> = { rc: 'RC pescador', viaje: 'Viaje', embarcacion: 'Embarcación' };

const STATUS_LABEL: Record<Status, string> = {
  awaiting_payment: 'Espera pago',
  quote_requested: 'Presupuesto pedido',
  quoted: 'Presupuestado',
  payment_reported: 'Pagado · falta certificado',
  delivered: 'Entregado',
  cancelled: 'Cancelado',
};

const STATUS_TONE: Record<Status, string> = {
  awaiting_payment: 'text-amber-500',
  quote_requested: 'text-blue-400',
  quoted: 'text-blue-500',
  payment_reported: 'text-rose-500 font-semibold',
  delivered: 'text-emerald-500',
  cancelled: 'text-muted-foreground',
};

const DETAIL_LABEL: Record<string, string> = {
  zone: 'Zona',
  destination: 'Destino',
  days: 'Días de viaje (aproximado)',
  startDate: 'Salida',
  endDate: 'Vuelta',
  travelers: 'Personas',
  fishingType: 'Tipo de pesca',
  boatType: 'Tipo',
  lengthM: 'Eslora (m)',
  enginePowerCv: 'Potencia (CV)',
  year: 'Año',
  valueEur: 'Valor (€)',
  coverage: 'Cobertura',
  navigationZone: 'Zona de navegación',
  homePort: 'Puerto base',
  registration: 'Matrícula',
  notes: 'Comentarios',
};

const STATUS_FILTERS: { value: '' | Status; label: string }[] = [
  { value: '', label: 'Todas' },
  { value: 'payment_reported', label: 'Pagadas' },
  { value: 'awaiting_payment', label: 'Espera pago' },
  { value: 'quote_requested', label: 'Presupuesto pedido' },
  { value: 'quoted', label: 'Presupuestadas' },
  { value: 'delivered', label: 'Entregadas' },
  { value: 'cancelled', label: 'Canceladas' },
];

function fmtEur(cents: number | null): string {
  if (cents == null) return '—';
  return (cents / 100).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '\u00a0€';
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
}

async function openFile(id: string, op: 'receipt' | 'id-front' | 'id-back'): Promise<void> {
  const preview = window.open('about:blank', '_blank');
  const token = await apiClient.getValidToken();
  const res = await fetch(`${API_BASE_URL}/ops/insurance-requests/${encodeURIComponent(id)}/${op}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    preview?.close();
    throw new Error('No se pudo abrir el justificante');
  }
  const url = URL.createObjectURL(await res.blob());
  if (preview) preview.location.replace(url);
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function DetailDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'insurance-request', id],
    queryFn: async () => {
      const { data: detail, error: err } = await apiClient.get<Detail>(`/ops/insurance-requests/${id}`);
      if (err) throw new Error(err.message);
      return detail as Detail;
    },
  });

  const action = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const { error: err } = await apiClient.post(`/ops/insurance-requests/${id}/action`, body);
      if (err) throw new Error(err.message);
    },
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'insurance-requests'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'insurance-request', id] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const closed = data?.status === 'delivered' || data?.status === 'cancelled';
  const canQuote = data && data.product !== 'rc' && (data.status === 'quote_requested' || data.status === 'quoted');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-border bg-background p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold">{data?.publicId || 'Solicitud'}</h3>
            {data ? (
              <p className="text-sm text-muted-foreground">
                {PRODUCT_LABEL[data.product]} · <span className={STATUS_TONE[data.status]}>{STATUS_LABEL[data.status]}</span>
              </p>
            ) : null}
          </div>
          <button type="button" onClick={onClose} className="text-muted-foreground" aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>

        {isLoading || !data ? (
          <Loader2 className="animate-spin" size={18} />
        ) : (
          <>
            <section className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <span className="text-muted-foreground">Contacto</span>
              <span>{data.contactName}</span>
              <span className="text-muted-foreground">Email</span>
              <span>{data.contactEmail}</span>
              <span className="text-muted-foreground">Teléfono</span>
              <span>{data.contactPhone}</span>
              <span className="text-muted-foreground">Importe</span>
              <span>{fmtEur(data.amountCents)}</span>
              <span className="text-muted-foreground">Concepto</span>
              <span>{data.paymentConcept || '—'}</span>
              <span className="text-muted-foreground">Justificante</span>
              <span>
                {data.hasReceipt ? (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-primary underline"
                    onClick={() => void openFile(data.id, 'receipt').catch((e: Error) => setError(e.message))}
                  >
                    <FileText size={14} /> Abrir
                  </button>
                ) : data.paymentChannel === 'whatsapp' ? (
                  'Por WhatsApp'
                ) : (
                  '—'
                )}
              </span>
              <span className="text-muted-foreground">Documento</span>
              <span>
                {data.hasIdentity && data.idChannel !== 'whatsapp' ? (
                  <span className="inline-flex gap-3">
                    <button type="button" className="text-primary underline" onClick={() => void openFile(data.id, 'id-front').catch((e: Error) => setError(e.message))}>Anverso</button>
                    <button type="button" className="text-primary underline" onClick={() => void openFile(data.id, 'id-back').catch((e: Error) => setError(e.message))}>Reverso</button>
                  </span>
                ) : data.idChannel === 'whatsapp' ? (
                  'Por WhatsApp'
                ) : (
                  '—'
                )}
              </span>
              <span className="text-muted-foreground">Correo a GESA</span>
              <span>{fmtDate(data.brokerEmailedAt)}</span>
            </section>

            {data.persons.length ? (
              <section className="space-y-2">
                <h4 className="text-sm font-semibold">Asegurados</h4>
                {data.persons.map((p, i) => (
                  <div key={i} className="rounded-md border border-border p-2 text-sm">
                    {p.firstName} {p.lastName} · {p.documentType.toUpperCase()} {p.documentNumber} · {p.birthDate} ·{' '}
                    {p.nationality}
                  </div>
                ))}
                {data.address ? (
                  <p className="text-sm text-muted-foreground">
                    {data.address.street}, {data.address.postalCode} {data.address.city} ({data.address.province})
                  </p>
                ) : null}
              </section>
            ) : null}

            {Object.keys(data.details || {}).length ? (
              <section className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                {Object.entries(data.details).map(([k, v]) => (
                  <div key={k} className="contents">
                    <span className="text-muted-foreground">{DETAIL_LABEL[k] || k}</span>
                    <span>{String(v)}</span>
                  </div>
                ))}
              </section>
            ) : null}

            {error ? <p className="text-sm text-rose-500">{error}</p> : null}

            {!closed ? (
              <section className="space-y-3 border-t border-border pt-3">
                {canQuote ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      className="w-32 rounded-md border border-border bg-background px-2 py-1 text-sm"
                      placeholder="Precio €"
                      inputMode="decimal"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                    />
                    <button
                      type="button"
                      disabled={action.isPending || !amount}
                      className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50"
                      onClick={() =>
                        action.mutate({
                          action: 'quote',
                          amountCents: Math.round(Number(amount.replace(',', '.')) * 100),
                        })
                      }
                    >
                      Enviar precio al cliente
                    </button>
                  </div>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={action.isPending}
                    className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm text-white disabled:opacity-50"
                    onClick={() => action.mutate({ action: 'deliver' })}
                  >
                    Marcar entregado
                  </button>
                  {data.status !== 'awaiting_payment' ? (
                    <button
                      type="button"
                      disabled={action.isPending}
                      className="rounded-md bg-muted px-3 py-1.5 text-sm disabled:opacity-50"
                      onClick={() => action.mutate({ action: 'resend_broker' })}
                    >
                      Reenviar a GESA
                    </button>
                  ) : null}
                  <button
                    type="button"
                    disabled={action.isPending}
                    className="rounded-md bg-muted px-3 py-1.5 text-sm text-rose-500 disabled:opacity-50"
                    onClick={() => action.mutate({ action: 'cancel' })}
                  >
                    Cancelar
                  </button>
                </div>
              </section>
            ) : null}

            <section className="space-y-2 border-t border-border pt-3">
              <textarea
                className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
                rows={2}
                placeholder={data.adminNote || 'Nota interna (nº de póliza, vencimiento…)'}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <button
                type="button"
                disabled={action.isPending || !note.trim()}
                className="rounded-md bg-muted px-3 py-1.5 text-sm disabled:opacity-50"
                onClick={() => action.mutate({ action: 'note', note }, { onSuccess: () => setNote('') })}
              >
                Guardar nota
              </button>
            </section>

            <section className="space-y-1 border-t border-border pt-3 text-xs text-muted-foreground">
              {data.events.map((ev, i) => (
                <p key={i}>
                  {fmtDate(ev.created_at)} · {ev.from_status || '—'} → {ev.to_status} · {ev.actor}
                  {ev.note ? ` · ${ev.note}` : ''}
                </p>
              ))}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

export function InsuranceRequestsPanel() {
  const [status, setStatus] = useState<'' | Status>('');
  const [product, setProduct] = useState<'' | Product>('');
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['admin', 'insurance-requests', status, product, search],
    queryFn: async () => {
      const { data: rows, error } = await dbClient.rpc<Row>('admin_insurance_requests_list', {
        p_status: status || null,
        p_product: product || null,
        p_search: search.trim() || null,
        p_limit: 200,
      });
      if (error) throw new Error(error.message);
      return normalizeRpcResponse(rows) as Row[];
    },
    staleTime: 30_000,
  });

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Seguros vía GESA. RC: el cliente paga a GESA y sube el justificante; con el pago sale el correo a GESA. Viaje y
        embarcación: presupuesto pedido a GESA al crear la solicitud; aquí se envía el precio al cliente. GESA manda el
        certificado o la póliza a pedidos@ y lo entregamos nosotros.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value || 'all'}
            type="button"
            onClick={() => setStatus(f.value)}
            className={cn(
              'px-3 py-1.5 rounded-full text-sm border transition-colors',
              status === f.value ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground',
            )}
          >
            {f.label}
          </button>
        ))}
        <select
          className="rounded-md border border-border bg-background px-2 py-1 text-sm"
          value={product}
          onChange={(e) => setProduct(e.target.value as '' | Product)}
        >
          <option value="">Todos los seguros</option>
          <option value="rc">RC pescador</option>
          <option value="viaje">Viaje</option>
          <option value="embarcacion">Embarcación</option>
        </select>
        <input
          className="rounded-md border border-border bg-background px-2 py-1 text-sm"
          placeholder="Buscar nº, nombre, email o teléfono"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button type="button" onClick={() => refetch()} className="text-sm text-muted-foreground" aria-label="Actualizar">
          <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
        </button>
      </div>
      {isLoading ? (
        <Loader2 className="animate-spin" size={18} />
      ) : !data?.length ? (
        <p className="text-sm text-muted-foreground">Todavía no hay solicitudes.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="py-1">Solicitud</th>
              <th>Seguro</th>
              <th>Estado</th>
              <th>Contacto</th>
              <th>Asegurados</th>
              <th>Importe</th>
              <th>Justificante</th>
              <th>Alta</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr
                key={row.id}
                className="cursor-pointer border-t border-border hover:bg-muted/40"
                onClick={() => setOpenId(row.id)}
              >
                <td className="py-1.5 font-medium">
                  {row.public_id}
                  {row.is_test ? <span className="ml-2 text-xs text-amber-500">test</span> : null}
                </td>
                <td>{PRODUCT_LABEL[row.product]}</td>
                <td className={STATUS_TONE[row.status]}>{STATUS_LABEL[row.status]}</td>
                <td>
                  {row.contact_name}
                  <span className="block text-xs text-muted-foreground">
                    {row.contact_email} · {row.contact_phone}
                  </span>
                </td>
                <td>{row.insured_count}</td>
                <td>{fmtEur(row.amount_cents)}</td>
                <td>{row.has_receipt ? 'Subido' : row.payment_channel === 'whatsapp' ? 'WhatsApp' : '—'}</td>
                <td>{fmtDate(row.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {openId ? <DetailDialog id={openId} onClose={() => setOpenId(null)} /> : null}
    </div>
  );
}
